import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { TransactionEvents } from '@app/domain/events';
import { AccountTransferDomainService } from '@app/domain/services';
import {
  GetAccountTransfersUseCase,
  GetAccountTransferByIdUseCase,
  GetAccountTransfersTotalAmountUseCase,
  CountAccountTransfersUseCase,
  CreateAccountTransferUseCase,
  UpdateAccountTransferUseCase,
  DeleteAccountTransferUseCase,
} from '@app/domain/use-cases';
import type { IAccountTransferRepository } from '@app/domain/ports';
import { ACCOUNT_TRANSFER_REPOSITORY } from '../../tokens/infrastructure.tokens';

/**
 * Provides AccountTransfer domain services and use cases.
 *
 * Includes:
 * - AccountTransferDomainService: Business rules and validation
 * - 7 Use Cases: GetAccountTransfers, GetAccountTransferById, GetTotal, Count, Create, Update, Delete
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideAccountTransferDomain(): Provider[] {
  return [
    // Domain Service
    {
      provide: AccountTransferDomainService,
      useClass: AccountTransferDomainService,
    },

    // Use Cases - Query
    {
      provide: GetAccountTransfersUseCase,
      useFactory: (repository: IAccountTransferRepository) => new GetAccountTransfersUseCase(repository),
      deps: [ACCOUNT_TRANSFER_REPOSITORY],
    },
    {
      provide: GetAccountTransferByIdUseCase,
      useFactory: (repository: IAccountTransferRepository) => new GetAccountTransferByIdUseCase(repository),
      deps: [ACCOUNT_TRANSFER_REPOSITORY],
    },
    {
      provide: GetAccountTransfersTotalAmountUseCase,
      useFactory: (repository: IAccountTransferRepository) => new GetAccountTransfersTotalAmountUseCase(repository),
      deps: [ACCOUNT_TRANSFER_REPOSITORY],
    },
    {
      provide: CountAccountTransfersUseCase,
      useFactory: (repository: IAccountTransferRepository) => new CountAccountTransfersUseCase(repository),
      deps: [ACCOUNT_TRANSFER_REPOSITORY],
    },

    // Use Cases - Commands
    {
      provide: CreateAccountTransferUseCase,
      useFactory: (repository: IAccountTransferRepository, domainService: AccountTransferDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new CreateAccountTransferUseCase(repository, domainService, dispatch);
      },
      deps: [ACCOUNT_TRANSFER_REPOSITORY, AccountTransferDomainService],
    },
    {
      provide: UpdateAccountTransferUseCase,
      useFactory: (repository: IAccountTransferRepository, domainService: AccountTransferDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new UpdateAccountTransferUseCase(repository, domainService, dispatch);
      },
      deps: [ACCOUNT_TRANSFER_REPOSITORY, AccountTransferDomainService],
    },
    {
      provide: DeleteAccountTransferUseCase,
      useFactory: (repository: IAccountTransferRepository, domainService: AccountTransferDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new DeleteAccountTransferUseCase(repository, domainService, dispatch);
      },
      deps: [ACCOUNT_TRANSFER_REPOSITORY, AccountTransferDomainService],
    },
  ];
}
